/*
 * @Author: Claude
 * @Date: 2026-03-11
 * @Description: AI Twin Generator SEO page - /ai-twin-generator
 */
import type { GetStaticProps } from 'next';
import React from 'react';

import SEOSections from '@/landing/ai-twin-generator/seo';
import { withLandingLayout } from '@/landing/components/LandingLayout';
import SEOHead, { type SEOHeadProps } from '@/landing/components/SEOHead';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createOrganizationSchema,
  createSoftwareAppSchema,
} from '@/landing/utils/structuredData';

const PAGE_URL = 'https://falcocut.ai/ai-twin-generator';

const FAQ_SCHEMA_DATA = [
  {
    question: 'What is an AI twin?',
    answer:
      'An AI twin is a digital replica of yourself created by artificial intelligence. It captures your appearance, expressions, and speaking style, allowing it to deliver scripts naturally in talking videos without requiring you to be on camera.',
  },
  {
    question: 'How do I create my AI twin?',
    answer:
      'Simply upload a clear photo of yourself, type your script, choose a voice (or clone your own), and click Generate. FalcoCut handles the rest, producing a fully lip-synced HD video in minutes.',
  },
  {
    question: 'Can an AI twin speak multiple languages?',
    answer:
      'Yes. FalcoCut supports over 30 languages and regional accents. Your AI twin can deliver scripts fluently in any supported language with perfect lip-sync.',
  },
  {
    question: 'Is the AI twin generator free?',
    answer:
      'FalcoCut offers a free plan that lets you try creating AI twin videos. For advanced features like voice cloning, HD/4K export, and unlimited generation, upgrade to a paid plan.',
  },
  {
    question: 'Can anyone else use my AI twin?',
    answer:
      'No. Your AI twin is private and secured to your account. We use top-level security to protect your face and voice data, ensuring your digital identity stays under your control.',
  },
];

const SEO_CONFIG: SEOHeadProps = {
  title: 'AI Twin Generator | Create Your Digital Twin Video | FalcoCut',
  description:
    'Turn your photo into a lifelike AI twin. Upload your image, type a script, and generate a realistic HD talking video in minutes with FalcoCut AI Twin Generator.',
  url: PAGE_URL,
  image: 'https://falcocut.ai/images/og-img.png',
  imageAlt: 'FalcoCut AI Twin Generator',
  publishedTime: '2026-03-11',
  modifiedTime: '2026-03-11',
  structuredData: [
    createSoftwareAppSchema({
      name: 'FalcoCut AI Twin Generator',
      description:
        'Turn your photo into a lifelike AI twin. Upload your image, type a script, and generate a realistic HD talking video in minutes.',
      url: PAGE_URL,
      applicationCategory: 'MultimediaApplication',
      ratingValue: '4.8',
      ratingCount: '3200',
      featureList: [
        'AI Digital Twin Creation',
        'Voice Cloning & Lip-Sync',
        'Photo to Talking Video',
        'Multi-language Support',
        'HD Video Export',
        'Batch Video Generation',
      ],
    }),
    createOrganizationSchema({
      name: 'FalcoCut',
      url: 'https://falcocut.ai',
      logo: 'https://falcocut.ai/images/logo.png',
    }),
    createFAQSchema({
      faqs: FAQ_SCHEMA_DATA,
    }),
    createBreadcrumbSchema({
      items: [
        { name: 'Home', url: 'https://falcocut.ai' },
        { name: 'AI Twin Generator', url: PAGE_URL },
      ],
    }),
  ],
};

const AITwinGeneratorPage: React.FC = () => {
  return (
    <>
      <SEOHead {...SEO_CONFIG} />
      <SEOSections />
    </>
  );
};

export default withLandingLayout(AITwinGeneratorPage);

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
    revalidate: 3600,
  };
};
